import { useContext } from 'react'

import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { TariffDetailsModel } from '../models/single-tariff-model'
import { TariffDetailsDto } from './dto/single-tariff.dto'
import { tariffDetailsDtoToModel } from '../transformers/single-tariff-dto-to-model'
import { DynamicPowerPricesEnabledContext } from '../../../dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { MOCK_DYNAMIC_TARIFF } from 'src/Libs/e-mobility/guided-tour'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchSingleTariffHook {
  tariff: TariffDetailsModel
  isLoading: boolean
  isError: boolean
  isRefetching: boolean
  isSuccess: boolean
}

/**
 * Function for fetching single tariff data
 * @returns tariff as TariffDetailsModel, isLoading as boolean, isSuccess as boolean, isRefetching as boolean and isError as boolean
 */
export const useFetchSingleTariff = (installationId: number, tariffId: string): UseFetchSingleTariffHook => {
  const authFetch = useAuthFetchWithResult()
  const { isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)
  const eMobilityTourRunning = useTypedSelector(state => state.eMobility.tour.isRunning)

  const fetchTariff = (installationId: number, tariffId: string): Promise<TariffDetailsDto> => {
    const singleTariffResponse = authFetch<TariffDetailsDto>({
      path: `tariffs/installation/${installationId}/tariff/${tariffId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'GET',
    })

    return singleTariffResponse.then(response => response)
  }

  const { data, isLoading, isError, isRefetching, isSuccess } = usePortalQuery({
    queryFn: () => (eMobilityTourRunning ? Promise.resolve(MOCK_DYNAMIC_TARIFF) : fetchTariff(Number(installationId), tariffId)),
    initialData: eMobilityTourRunning ? MOCK_DYNAMIC_TARIFF : undefined,
    queryKey: ['tariffDetails', tariffId],
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })

  const tariff = tariffDetailsDtoToModel(data ?? undefined, isDynamicPowerPriceEnabledForWallboxes)

  return { tariff, isLoading, isError, isRefetching, isSuccess }
}
